import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMenuActiveState } from './useMenuActiveState';

import type { Menus } from '@kc-console/context';

import { useRegionEndpoint } from '@/shared/hooks';

export interface UseServiceListItemParams {
  menu: Menus;
  onClick?: () => void;
}

export const useServiceListItem = ({ menu, onClick }: UseServiceListItemParams) => {
  const router = useRouter();
  const { currentRegion } = useRegionEndpoint();
  const { path, name: menuName, subMenus } = menu;

  const isSelected = useMenuActiveState(menu);

  const hasSubMenu = (subMenus?.length ?? 0) > 0;

  const [isOpen, setOpen] = useState(hasSubMenu && router.asPath.startsWith(path));

  const isChildActive = useMemo(() => {
    if (!hasSubMenu) {
      return false;
    }
    const currentPath = router.asPath.split('?')[0];
    return currentPath.startsWith(path);
  }, [hasSubMenu, router.asPath, path]);

  const isActive = isSelected || (!isOpen && isChildActive);

  const itemButtonTag = useMemo(() => {
    if (hasSubMenu) {
      return 'button';
    }
    return 'nextLink';
  }, [hasSubMenu]);

  const handleClick = useCallback(() => {
    if (itemButtonTag === 'button') {
      setOpen(prev => !prev);
    }
    onClick?.();
  }, [itemButtonTag, onClick]);

  useEffect(() => {
    if (hasSubMenu && router.asPath.startsWith(path)) {
      setOpen(true);
    }
  }, [router.asPath, path, hasSubMenu]);

  return {
    path: `${path}?region=${currentRegion}`,
    menuName,
    subMenus,
    hasSubMenu,
    isOpen,
    isActive,
    handleClick,
  };
};
